import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { cn } from '../../lib/utils';
import { ArrowLeft, ArrowRight, CheckCircle, Info, Check } from 'lucide-react';

interface IntegrationStepProps {
  onNext: (data: { integrationType: 'sdk' | 'shopify' | 'woocommerce' | 'manual' }) => void;
  onBack: () => void;
  initialData?: {
    integrationType?: 'sdk' | 'shopify' | 'woocommerce' | 'manual';
  };
}

const integrations = [
  {
    id: 'sdk' as const,
    name: 'JavaScript SDK',
    icon: '💻',
    description: 'Best for web applications and SPAs',
    features: ['Real-time translation', 'Automatic detection', 'Offline support'],
    recommended: true,
    difficulty: 'Easy',
  },
  {
    id: 'shopify' as const,
    name: 'Shopify App',
    icon: '🛍️',
    description: 'One-click installation for Shopify stores',
    features: ['No code required', 'Auto-sync', 'Theme integration'],
    recommended: false,
    difficulty: 'Easiest',
  },
  {
    id: 'woocommerce' as const,
    name: 'WooCommerce Plugin',
    icon: '🔌',
    description: 'WordPress plugin for WooCommerce',
    features: ['WordPress admin', 'Product sync', 'SEO friendly'],
    recommended: false,
    difficulty: 'Easy',
  },
  {
    id: 'manual' as const,
    name: 'REST API',
    icon: '⚙️',
    description: 'Full control via REST API',
    features: ['Complete flexibility', 'Custom integration', 'Any platform'],
    recommended: false,
    difficulty: 'Advanced',
  },
];

const IntegrationStep: React.FC<IntegrationStepProps> = ({
  onNext,
  onBack,
  initialData,
}) => {
  const [selected, setSelected] = useState<typeof integrations[0]['id'] | null>(
    initialData?.integrationType || null
  );

  const handleContinue = () => {
    if (selected) {
      onNext({ integrationType: selected });
    }
  };

  return (
    <CardContent className="p-12">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full text-3xl mb-4">
            🔌
          </div>
          <h2 className="text-3xl font-bold text-foreground mb-2">
            Choose Your Integration
          </h2>
          <p className="text-muted-foreground">
            How would you like to connect LocalizationHub to your app?
          </p>
        </div>

        {/* Integration Options */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {integrations.map((integration) => (
            <button
              key={integration.id}
              onClick={() => setSelected(integration.id)}
              className={cn(
                "relative p-6 rounded-xl border-2 text-left transition-all",
                selected === integration.id
                  ? "border-primary bg-primary/5 shadow-lg"
                  : "border-border hover:border-muted-foreground hover:shadow-md"
              )}
            >
              {/* Recommended Badge */}
              {integration.recommended && (
                <Badge className="absolute top-3 right-3 bg-primary">
                  Recommended
                </Badge>
              )}

              {/* Icon & Title */}
              <div className="flex items-start mb-3">
                <div className="text-4xl mr-4">{integration.icon}</div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-foreground mb-1">
                    {integration.name}
                  </h3>
                  <p className="text-sm text-muted-foreground mb-2">
                    {integration.description}
                  </p>
                  <Badge
                    variant="secondary"
                    className={cn(
                      integration.difficulty === 'Easiest' && 'bg-green-100 text-green-700',
                      integration.difficulty === 'Easy' && 'bg-blue-100 text-blue-700',
                      integration.difficulty === 'Advanced' && 'bg-purple-100 text-purple-700'
                    )}
                  >
                    {integration.difficulty}
                  </Badge>
                </div>
              </div>

              {/* Features */}
              <ul className="space-y-2 mt-4">
                {integration.features.map((feature, index) => (
                  <li key={index} className="flex items-center text-sm text-muted-foreground">
                    <Check className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
                    {feature}
                  </li>
                ))}
              </ul>

              {/* Selected Indicator */}
              {selected === integration.id && (
                <div className="absolute bottom-3 right-3">
                  <CheckCircle className="w-8 h-8 text-primary" />
                </div>
              )}
            </button>
          ))}
        </div>

        {/* Info Box */}
        <Card className="bg-gradient-to-r from-primary/5 to-primary/10 border-0 mb-8">
          <CardContent className="p-6">
            <div className="flex items-start">
              <Info className="h-5 w-5 text-primary mr-3 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-semibold text-foreground mb-2">
                  Don't worry, you can change this later
                </h4>
                <p className="text-sm text-muted-foreground">
                  You can use multiple integration methods simultaneously. Start with the
                  easiest option and add more as needed.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Navigation */}
        <div className="flex items-center justify-between pt-6 border-t">
          <Button type="button" variant="ghost" onClick={onBack}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>

          <Button onClick={handleContinue} disabled={!selected}>
            Continue
            <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        </div>
      </div>
    </CardContent>
  );
};

export default IntegrationStep;
